import { Box, Button, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Popover, Select, SxProps, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { client } from "src/utils/axios-client";
import { SelectOption } from "src/types/common";

const loadOptions = () => client.get(`/api/options/STAGE`).then((res) => res.data);


type Props = {
    value: string,
    error?: boolean,
    onChange: (newValue: string) => void,
    sx?: SxProps,
    extended?: boolean,
    label?: string
}

const basicColors = [
    { value: 'black', name: 'black' },
    { value: 'silver', name: 'silver' },
    { value: 'gray', name: 'gray' },
    { value: 'white', name: 'white' },
    { value: 'maroon', name: 'maroon' },
    { value: 'red', name: 'red' },
    { value: 'orange', name: 'orange' },
    { value: 'purple', name: 'purple' },
    { value: 'fuchsia', name: 'fuchsia' },
    { value: 'green', name: 'green' },
    { value: 'lime', name: 'lime' },
    { value: 'olive', name: 'olive' },
    { value: 'yellow', name: 'yellow' },
    { value: 'navy', name: 'navy' },
    { value: 'blue', name: 'blue' },
    { value: 'teal', name: 'teal' },    
    { value: 'aqua', name: 'aqua' },    
] as SelectOption[]

const extendedColors = [
    { value: 'indianred', name: 'indianred' },
    { value: 'lightcoral', name: 'lightcoral' },
    { value: 'salmon', name: 'salmon' },
    { value: 'darksalmon', name: 'darksalmon' },
    { value: 'lightsalmon', name: 'lightsalmon' },
    { value: 'crimson', name: 'crimson' },
    { value: 'red', name: 'red' },
    { value: 'firebrick', name: 'firebrick' },
    { value: 'darkred', name: 'darkred' },
    { value: 'pink', name: 'pink' },
    { value: 'lightpink', name: 'lightpink' },
    { value: 'hotpink', name: 'hotpink' },
    { value: 'deeppink', name: 'deeppink' },
    { value: 'mediumvioletred', name: 'mediumvioletred' },
    { value: 'palevioletred', name: 'palevioletred' },
    { value: 'lightsalmon', name: 'lightsalmon' },
    { value: 'coral', name: 'coral' },
    { value: 'tomato', name: 'tomato' },
    { value: 'orangered', name: 'orangered' },
    { value: 'darkorange', name: 'darkorange' },
    { value: 'orange', name: 'orange' },
    { value: 'yellow', name: 'yellow' },
    { value: 'lightyellow', name: 'lightyellow' },
    { value: 'lemonchiffon', name: 'lemonchiffon' },
    { value: 'lightgoldenrodyellow', name: 'lightgoldenrodyellow' },
    { value: 'papayawhip', name: 'papayawhip' },
    { value: 'moccasin', name: 'moccasin' },
    { value: 'peachpuff', name: 'peachpuff' },
    { value: 'palegoldenrod', name: 'palegoldenrod' },
    { value: 'khaki', name: 'khaki' },
    { value: 'darkkhaki', name: 'darkkhaki' },
    { value: 'lavender', name: 'lavender' },
    { value: 'thistle', name: 'thistle' },
    { value: 'plum', name: 'plum' },
    { value: 'violet', name: 'violet' },
    { value: 'orchid', name: 'orchid' },
    { value: 'fuchsia', name: 'fuchsia' },
    { value: 'magenta', name: 'magenta' },
    { value: 'mediumorchid', name: 'mediumorchid' },
    { value: 'mediumpurple', name: 'mediumpurple' },
    { value: 'rebeccapurple', name: 'rebeccapurple' },
    { value: 'blueviolet', name: 'blueviolet' },
    { value: 'darkviolet', name: 'darkviolet' },
    { value: 'darkorchid', name: 'darkorchid' },
    { value: 'darkmagenta', name: 'darkmagenta' },
    { value: 'purple', name: 'purple' },
    { value: 'indigo', name: 'indigo' },
    { value: 'slateblue', name: 'slateblue' },
    { value: 'darkslateblue', name: 'darkslateblue' },
    { value: 'mediumslateblue', name: 'mediumslateblue' },
    { value: 'greenyellow', name: 'greenyellow' },
    { value: 'chartreuse', name: 'chartreuse' },
    { value: 'lawngreen', name: 'lawngreen' },
    { value: 'lime', name: 'lime' },
    { value: 'limegreen', name: 'limegreen' },
    { value: 'palegreen', name: 'palegreen' },
    { value: 'lightgreen', name: 'lightgreen' },
    { value: 'mediumspringgreen', name: 'mediumspringgreen' },
    { value: 'springgreen', name: 'springgreen' },
    { value: 'mediumseagreen', name: 'mediumseagreen' },
    { value: 'seagreen', name: 'seagreen' },
    { value: 'forestgreen', name: 'forestgreen' },
    { value: 'green', name: 'green' },
    { value: 'darkgreen', name: 'darkgreen' },
    { value: 'yellowgreen', name: 'yellowgreen' },
    { value: 'olivedrab', name: 'olivedrab' },
    { value: 'olive', name: 'olive' },
    { value: 'darkolivegreen', name: 'darkolivegreen' },
    { value: 'mediumaquamarine', name: 'mediumaquamarine' },
    { value: 'darkseagreen', name: 'darkseagreen' },
    { value: 'lightseagreen', name: 'lightseagreen' },
    { value: 'darkcyan', name: 'darkcyan' },
    { value: 'teal', name: 'teal' },
    { value: 'aqua', name: 'aqua' },
    { value: 'cyan', name: 'cyan' },
    { value: 'lightcyan', name: 'lightcyan' },
    { value: 'paleturquoise', name: 'paleturquoise' },
    { value: 'aquamarine', name: 'aquamarine' },
    { value: 'turquoise', name: 'turquoise' },
    { value: 'mediumturquoise', name: 'mediumturquoise' },
    { value: 'darkturquoise', name: 'darkturquoise' },
    { value: 'cadetblue', name: 'cadetblue' },
    { value: 'steelblue', name: 'steelblue' },
    { value: 'lightsteelblue', name: 'lightsteelblue' },
    { value: 'powderblue', name: 'powderblue' },
    { value: 'lightblue', name: 'lightblue' },
    { value: 'skyblue', name: 'skyblue' },
    { value: 'lightskyblue', name: 'lightskyblue' },
    { value: 'deepskyblue', name: 'deepskyblue' },
    { value: 'dodgerblue', name: 'dodgerblue' },
    { value: 'cornflowerblue', name: 'cornflowerblue' },
    { value: 'mediumslateblue', name: 'mediumslateblue' },
    { value: 'royalblue', name: 'royalblue' },
    { value: 'blue', name: 'blue' },
    { value: 'mediumblue', name: 'mediumblue' },
    { value: 'darkblue', name: 'darkblue' },
    { value: 'navy', name: 'navy' },
    { value: 'midnightblue', name: 'midnightblue' },
    { value: 'cornsilk', name: 'cornsilk' },
    { value: 'blanchedalmond', name: 'blanchedalmond' },
    { value: 'bisque', name: 'bisque' },
    { value: 'navajowhite', name: 'navajowhite' },
    { value: 'wheat', name: 'wheat' },
    { value: 'burlywood', name: 'burlywood' },
    { value: 'tan', name: 'tan' },
    { value: 'rosybrown', name: 'rosybrown' },
    { value: 'sandybrown', name: 'sandybrown' },
    { value: 'goldenrod', name: 'goldenrod' },
    { value: 'darkgoldenrod', name: 'darkgoldenrod' },
    { value: 'peru', name: 'peru' },
    { value: 'chocolate', name: 'chocolate' },
    { value: 'saddlebrown', name: 'saddlebrown' },
    { value: 'sienna', name: 'sienna' },
    { value: 'brown', name: 'brown' },
    { value: 'maroon', name: 'maroon' },
    { value: 'white', name: 'white' },
    { value: 'snow', name: 'snow' },
    { value: 'honeydew', name: 'honeydew' },
    { value: 'mintcream', name: 'mintcream' },
    { value: 'azure', name: 'azure' },
    { value: 'aliceblue', name: 'aliceblue' },
    { value: 'ghostwhite', name: 'ghostwhite' },
    { value: 'whitesmoke', name: 'whitesmoke' },
    { value: 'seashell', name: 'seashell' },
    { value: 'beige', name: 'beige' },
    { value: 'oldlace', name: 'oldlace' },
    { value: 'floralwhite', name: 'floralwhite' },
    { value: 'ivory', name: 'ivory' },
    { value: 'antiquewhite', name: 'antiquewhite' },
    { value: 'linen', name: 'linen' },
    { value: 'lavenderblush', name: 'lavenderblush' },
    { value: 'mistyrose', name: 'mistyrose' },
    { value: 'gainsboro', name: 'gainsboro' },
    { value: 'lightgray', name: 'lightgray' },
    { value: 'silver', name: 'silver' },
    { value: 'darkgray', name: 'darkgray' },
    { value: 'gray', name: 'gray' },
    { value: 'dimgray', name: 'dimgray' },
    { value: 'lightslategray', name: 'lightslategray' },
    { value: 'slategray', name: 'slategray' },
    { value: 'darkslategray', name: 'darkslategray' },
    { value: 'black', name: 'black' },
] as SelectOption[]

const ColorDropdown = ({ value, onChange, error = false, sx, extended = false,label }: Props) => {
    const colors = extended ? extendedColors : basicColors;

    return (
        <Select
            fullWidth
            label={label }            
            error={error}
            size="small"
            value={value}
            sx={{ ...sx }}
            
            onChange={(e) => onChange(e.target.value)}
        >
            {colors.map(item =>
                <MenuItem
                    key={item.value}
                    value={item.value}
                >
                    <div style={{ height: 25, width: '70%', backgroundColor: item.value }}></div>
                </MenuItem>
            )}
        </Select>
    )
}

export default ColorDropdown;